import CalendarFolder from 'owa-service/lib/contract/CalendarFolder';
import getCalculatedFolderId from './getCalculatedFolderId';
import GetCalendarFoldersResponse from 'owa-service/lib/contract/GetCalendarFoldersResponse';
import LocalCalendarEntry from 'owa-service/lib/contract/LocalCalendarEntry';
import { AllCalendarEntry } from '../schema/AllCalendarEntry';
import { Calendar } from '../schema/Calendar';
import { CalendarId } from '../schema/CalendarId';
import { EventToCalendarAssociation } from '../schema/EventToCalendarAssociation';
import { getAllowedOnlineMeetingProvidersForCalendarEntry } from './getAllowedOnlineMeetingProvidersForCalendarEntry';
import { getDefaultOnlineMeetingProvidersForCalendarEntry } from './getDefaultOnlineMeetingProviderForCalendarEntry';
import { getFilteredUserConfiguration } from 'mini-settings/lib/selectors/getFilteredUserConfiguration';
import { getUserMailboxInfo } from 'owa-client-ids';
import { isCalendarEntryForLocalCalendar } from './isCalendarEntryForLocalCalendar';
import { isCalendarEntryForPeopleCalendar } from './isCalendarEntryForPeopleCalendar';
import { isCalendarEntryForReadOnlyCalendar } from './isCalendarEntryForReadOnlyCalendar';
import { isCalendarEntryForWritableCalendar } from './isCalendarEntryForWritableCalendar';

export const extractEventCalendarAssociationFromResponse = (
    response: GetCalendarFoldersResponse
): EventToCalendarAssociation => {
    const { CalendarGroups, CalendarFolders } = response;

    const calendarFolderLookup = CalendarFolders?.reduce((lookup, folder) => {
        if (folder.FolderId?.Id) {
            lookup.set(folder.FolderId?.Id, folder);
        }
        return lookup;
    }, new Map<string, CalendarFolder>());

    const userMailboxInfo = getUserMailboxInfo();

    const userConfigurationSelectedCalendars = getFilteredUserConfiguration().ViewStateConfiguration
        .SelectedCalendarsTNarrow;

    const selectedCalendarIds = new Set([...(userConfigurationSelectedCalendars || [])]);

    return CalendarGroups.reduce(
        (initialCalendarsLookup, group) =>
            group.Calendars.reduce((calendarsLookup, calendarEntry: AllCalendarEntry) => {
                const calendarEntryId = calendarEntry.ItemId?.Id;

                const calculatedFolderId = getCalculatedFolderId(calendarEntry)?.Id;
                const calendarFolderFromResponse: CalendarFolder = calendarFolderLookup.get(
                    calculatedFolderId
                );

                const isLocalCalendar = isCalendarEntryForLocalCalendar(calendarEntry);
                const isPeopleCalendar = isCalendarEntryForPeopleCalendar(calendarEntry, group);
                const isReadOnly = isCalendarEntryForReadOnlyCalendar(calendarEntry);
                const isWritable: boolean = isCalendarEntryForWritableCalendar(
                    calendarEntry,
                    calendarFolderFromResponse
                );
                const AllowedOnlineMeetingProviders = getAllowedOnlineMeetingProvidersForCalendarEntry(
                    calendarEntry,
                    calendarFolderFromResponse
                );
                const DefaultOnlineMeetingProvider = getDefaultOnlineMeetingProvidersForCalendarEntry(
                    calendarEntry,
                    calendarFolderFromResponse
                );

                const isSelected = [calculatedFolderId, calendarEntryId].some((id, index) =>
                    selectedCalendarIds.has(id)
                );

                const calendar: Calendar = {
                    type: calendarEntry.__type,
                    id: { id: calendarEntryId, mailboxInfo: userMailboxInfo },
                    calculatedFolderId: calculatedFolderId,
                    displayName: calendarEntry.CalendarName,
                    calendarColor: calendarEntry.CalendarColor,
                    hexColorString: calendarEntry.HexColorString,
                    isSelected,
                    remoteCategories: calendarEntry.RemoteCategories,
                    CalendarFolderType: calendarEntry.CalendarFolderType,
                    SharedFolderId: calendarEntry.SharedFolderId,
                    isGroupMailboxCalendar: calendarEntry.IsGroupMailboxCalendar,
                    isGeneralScheduleCalendar: calendarEntry.IsGeneralScheduleCalendar,
                    isFolderIDValid: !!calendarFolderFromResponse,
                    SharedOwnerEmailAddress:
                        calendarEntry.SharedOwnerEmailAddress || calendarEntry.OwnerEmailAddress,
                    isPeopleCalendar,
                    isLocalCalendar,
                    isReadOnly,
                    isWritable,
                    AllowedOnlineMeetingProviders,
                    DefaultOnlineMeetingProvider,
                };

                const {
                    calendarIdToCalendarMapping,
                    folderIdToCalendarIdMapping,
                    calendars,
                } = calendarsLookup;

                calendarIdToCalendarMapping.set(calendarEntryId, calendar);
                folderIdToCalendarIdMapping.set(calculatedFolderId, {
                    id: calendarEntryId,
                    mailboxInfo: userMailboxInfo,
                });
                calendars.push(calendar);

                if ((calendarEntry as LocalCalendarEntry).IsDefaultCalendar) {
                    calendarsLookup.defaultCalendar = calendar;
                }

                return calendarsLookup;
            }, initialCalendarsLookup),
        {
            calendarIdToCalendarMapping: new Map<string, Calendar>(),
            folderIdToCalendarIdMapping: new Map<string, CalendarId>(),
            calendars: new Array<Calendar>(),
            defaultCalendar: undefined,
        }
    );
};
